import { useCallback, useRef, useState } from 'react';
import { message } from 'antd';

import { GetHistoryKPI } from '@/apis/aiHistory';
import useUnauthorize from '@/hooks/useUnauthorize';
import { ChartDataItem } from './type';
import { HistoryPayload } from './utils/buildHistoryPayload';

// Skips refetching an identical payload (e.g. paging) and drops responses superseded by a newer request.
export function useHistoryKpi(token: string) {
  const { unauthorize } = useUnauthorize();
  const [kpiData, setKpiData] = useState<ChartDataItem[]>([]);
  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);

  const fetchKpi = useCallback(
    (payload: HistoryPayload) => {
      const key = JSON.stringify([token, payload]);
      if (key === lastKeyRef.current) return;
      lastKeyRef.current = key;
      const requestId = ++requestIdRef.current;
      const isStale = () => requestId !== requestIdRef.current;

      GetHistoryKPI(token, payload)
        .then((res) => {
          if (isStale()) return;
          const data = res?.data?.data;
          if (data?.is_success) {
            setKpiData(data?.data || []);
            return;
          }
          lastKeyRef.current = null;
          message.error(data?.message || 'Failed to fetch KPI');
        })
        .catch((error) => {
          if (isStale()) return;
          lastKeyRef.current = null;
          unauthorize(error);
          message.error('Failed to fetch KPI');
        });
    },
    [token, unauthorize]
  );

  // Drops any in-flight response and forgets the last payload so the same payload can be fetched again.
  const invalidate = useCallback(() => {
    requestIdRef.current += 1;
    lastKeyRef.current = null;
  }, []);

  return { kpiData, fetchKpi, invalidate };
}
